/** @license
 *
 * Copyright 2020 Esri
 *
 * All rights reserved under the copyright laws of the United States and applicable international laws, treaties,
 * and conventions.
 *
 * This material is licensed for use under the Esri Master License Agreement (MLA), and is bound by the terms of
 * that agreement. You may redistribute and use this code without modification, provided you adhere to the terms
 * of the MLA and include this copyright notice.
 *
 * See use restrictions at http://www.esri.com/legal/pdfs/mla_e204_e300/english
 *
 * For additional information, contact: Environmental Systems Research Institute, Inc. Attn: Contracts and Legal
 * Services Department 380 New York Street Redlands, California, USA 92373 USA
 *
 * email: contracts@esri.com
 */

//#region Interfaces

import * as common from "@esri/solution-common";
import * as helpers from "../src/helpers";
import * as restTypes from "@esri/arcgis-rest-types";

export interface ICacheBatch<T> {
  cards: T[];
  paging: restTypes.IPagedResponse;
}

export interface IKeyedStrings {
  [key: string]: string;
}

export interface IPreview {
  name: string;
  format: string;
  alt: string;
}

/**
 * Summary of a solution item.
 */
export interface ISolutionInfoCard extends helpers.ISortableItem {
  id: string;
  title: string;
  snippet: string;
  description: string;
  url: string;
  itemUrl: string;
  thumbnailFilename: string;
  thumbnailUrl: string;
  tryitUrl: string;
  typeKeywords: string[];
  learnMoreUrl: string;
  createdMs: number;
  created: string;
  modified: string;
  numViews: number;
  tags: string[];
  categories: string[];
  licenseInfo: string;
  deployCommonId: string;
  deployVersion: string;
  canDelete: boolean;
  canDeploy: boolean;
  preview?: IPreview[];
  sourcePortalUrl: string;
  knownToBePublicSource: boolean;
  waitId: string;
  relatedSolutions: string[];
}

//#endregion
// ------------------------------------------------------------------------------------------------------------------ //

/**
 * Converts a set of solution ids into a set of item ids.
 *
 * @param solutionIds Solution ids to convert
 * @param solutionIdMappingToItemId Mapping from solution id to item id
 * @returns Matching item ids
 */
export function convertSolutionIdsToItemIds(
  solutionIds: string[],
  solutionIdMappingToItemId: IKeyedStrings
): string[] {
  const itemIds: string[] = [];

  solutionIds.forEach(
    (solutionId) => {
      const itemId = solutionIdMappingToItemId[solutionId];
      if (itemId) {
        itemIds.push(itemId);
      }
    }
  );

  return itemIds;
}

/**
 * Creates a subset of a cache in which every item in the subcache has an id in the acceptableIds list.
 *
 * @param sourceCache Cache to filter
 * @param acceptableIds Set of ids that can be in the subcache
 * @returns Filtered cache
 */
export function createFilteredSubcache(
  sourceCache: ISolutionInfoCard[],
  acceptableIds: Set<string>
): ISolutionInfoCard[] {
  const cardSubcache = [] as ISolutionInfoCard[];
  sourceCache.forEach(card => {
    if (acceptableIds.has(card.id)) {
      cardSubcache.push(card);
    }
  });
  return cardSubcache;
}

/**
 * Extracts a range of entries from a cache.
 *
 * @param cache Source
 * @param start1 One-based index into cache
 * @param num Number of items to extract
 * @returns Subset of cache and paging information
 */
export function extractFromCache<T>(
  cache: T[],
  start1: number,
  num: number
): ICacheBatch<T> {
  // Handle bad start as nothing found
  if (start1 < 1 || cache.length < start1) {
    return {
      cards: [],
      paging: {
        start: start1,
        num: 0,
        nextStart: -1,
        total: cache.length
      } as restTypes.IPagedResponse
    }
  }

  // Trim excess request to cache length
  const end1 = Math.min(start1 + num, cache.length + 1); // [start..end), 1-based index
  const nextStart = end1 < cache.length ? end1 : -1;

  // Assemble extract
  return {
    cards: cache.slice(start1 - 1, end1 - 1),
    paging: {
      start: start1,
      num: end1 - start1,
      nextStart: nextStart,
      total: cache.length
    } as restTypes.IPagedResponse
  } as ICacheBatch<T>;
}

/**
 * Determines the session to use for a card based on the card's source portal URL and the user's session, if any.
 *
 * @param card Card to determine session for
 * @param userSession User session
 * @param anonAgolSession Anonymous AGO session
 * @returns userSession or anonAgolSession, depending on the card's source portal URL and if the user is signed in
 */
export function getCardSession(
  card: ISolutionInfoCard,
  userSession: common.UserSession | undefined,
  anonAgolSession: common.UserSession | undefined
): common.UserSession | undefined {
  // User is signed in
  if (userSession) {
    // Return the user session if the user is from AGO
    if (userSession.portal.indexOf("arcgis.com") > 0) {
      return userSession

    // Return the anonymous AGO session if the card is from AGO
    } else if (card.sourcePortalUrl === "https://www.arcgis.com") {
      return anonAgolSession;

    // Return the user session if the card is from the same portal as the user
    } else {
      return userSession;
    }

  // User is not signed in
  } else {
    return anonAgolSession;
  }
}

/**
 * Gets the related solutions for a set of cards.
 *
 * @param cards Cards that may have related solutions
 * @param cardsToSearch Cards to search for related solutions
 * @returns Set of deduped item ids of related solutions
 */
export function getDeployedRelatedSolutionIds(
  cards: ISolutionInfoCard[],
  cardsToSearch: ISolutionInfoCard[]
): Set<string> {
  // Extract the solution ids from the supplied cards
  const cardSolutionIds = getSolutionIds(cards);

  // Get the solutions related to these solution ids
  return getRelatedSolutions(cardSolutionIds, cardsToSearch);
}

/**
 * Searches a set of cards matching supplied solution ids and returns the related solution item ids for all matches.
 *
 * @param solutionIds Solutions to query for related solutions
 * @param cards Cards to search for matches in list of solutionIds
 * @returns Set of deduped item ids of related solutions
 */
export function getRelatedSolutions(
  solutionIds: string[],
  cards: ISolutionInfoCard[]
): Set<string> {
  // Run through the cache extracting the relatedSolutions property from each card
  let relatedSolutions = [] as string[];

  cards.forEach(
    card => {
      const solutionId = getSolutionId(card);
      if (solutionId && solutionIds.indexOf(solutionId) >= 0 && card.relatedSolutions) {
        // Add the related solution item ids
        relatedSolutions = relatedSolutions.concat(card.relatedSolutions);
      }
    }
  );

  // Return without duplicates
  return new Set(relatedSolutions);
}

/**
 * Gets the solution id of a card.
 *
 * @param card Card to examine
 * @returns Solution id of card
 */
export function getSolutionId(
  card: ISolutionInfoCard
): string | null {
  const solutionIdPrefix = "solutionid-";
  const solutionIDList = card.typeKeywords.filter(
    keyword => keyword.startsWith(solutionIdPrefix)
  );

  let solutionId = null;
  if (Array.isArray(solutionIDList) && solutionIDList.length > 0) {
    solutionId = solutionIDList[0].substring(solutionIdPrefix.length);
  }

  return solutionId;
}

/**
 * Gets the solution ids of a list of cards.
 *
 * @param cards Cards to examine
 * @returns Solution ids of cards
 */
export function getSolutionIds(
  cards: ISolutionInfoCard[]
): string[] {
  // Run through the cards extracting the solution id property from each card
  let solutionIds = [] as string[];

  cards.forEach(
    card => {
      const solutionId = getSolutionId(card);
      if (solutionId) {
        solutionIds.push(solutionId);
      }
    }
  );

  // Dedup
  solutionIds = Array.from(new Set(solutionIds));

  return solutionIds;
}

/**
 * Sorts a cache in-place based on a sort field and a sorting order.
 *
 * @param cache Cache to sort in-place
 * @param sortField Cache item field to use for sort: "created" (deployed Solutions only), "numviews",
 *  "relevance" (default), "title"
 * @param sortOrder Sort direction for title and numviews fields: "asc" or "desc" (default)
 * @returns The sorted cardCache input parameter
 */
export function sortCache(
  cache: helpers.ISortableItem[],
  sortField: string,
  sortOrder: string
): helpers.ISortableItem[] {
  // Returns -1 if item1 is before item2; 0 to keep original order; 1 if item2 is before item1
  let compareFcn: (item1: helpers.ISortableItem, item2: helpers.ISortableItem) => number;
  const direction = sortOrder === "asc" ? 1 : -1; // for inverting the results of a compare function if order is "desc"

  switch (sortField) {

    case "created":
      compareFcn =
        (item1: helpers.ISortableItem, item2: helpers.ISortableItem) =>
        direction * (item1.createdMs < item2.createdMs ? -1 : item1.createdMs > item2.createdMs ? 1 : 0);
      break;

    case "numviews":
      compareFcn =
        (item1: helpers.ISortableItem, item2: helpers.ISortableItem) =>
        direction * (item1.numViews < item2.numViews ? -1 : item1.numViews > item2.numViews ? 1 : 0);
      break;

    case "title":
      compareFcn =
        (item1: helpers.ISortableItem, item2: helpers.ISortableItem) =>
          direction * (item1.title.toLowerCase() < item2.title.toLowerCase()
            ? -1
            : item1.title.toLowerCase() > item2.title.toLowerCase()
              ? 1
              : 0);
      break;

    default: // "relevance"; only supports descending order
      // A sort on relevance considers many factors, such as the presence of a search keyword in the item's tags,
      // the frequency of a keyword's appearance in item information fields (title, summary, and description),
      // the completeness of the item information, and the item's view count.
      // -- https://pro.arcgis.com/en/pro-app/latest/help/projects/search-portal-items.htm
      compareFcn =
        () => 0 // for the time being, we're not sorting by relevance
      break;

  }

  cache.sort(compareFcn);
  return cache;
}

/**
 * Gets the related solutions for a card and adds their corresponding item ids to a supplied set of item ids.
 *
 * @param deployedCard Card that may have related solutions
 * @param cardsToSearch Cards to search for related solutions
 * @param relatedSolutions Set of item ids of related solutions
 * @returns Updated set of deduped item ids of related solutions
 */
export function updateDeployedRelatedSolutionIds(
  deployedCard: ISolutionInfoCard,
  cardsToSearch: ISolutionInfoCard[],
  relatedSolutions: Set<string>
): Set<string> {
  // Extract the solution id from the supplied card
  const deployedSolutionId = getSolutionId(deployedCard);

  if (deployedSolutionId) {
    // Get the solutions related to this solution id
    const cardRelatedSolutions = getRelatedSolutions([deployedSolutionId], cardsToSearch);

    // Merge the result into the set of related solutions
    return new Set([ ...relatedSolutions, ...cardRelatedSolutions ]);
  }

  return relatedSolutions;
}
